import React from 'react';
import { motion } from 'framer-motion';
import './Legal.css';

const PrivacyPolicy = () => {
  return (
    <motion.div 
      className="legal-content"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="legal-header">
        <a href="/" className="back-link">← Back to Home</a>
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last updated: April 16, 2026</p>
      </div>
      
      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 }}
      >
        <h2>1. Introduction</h2>
        <p>Welcome to Grindon Gym. We are committed to protecting your personal information and your right to privacy. If you have any questions or concerns about our policy, or our practices with regards to your personal information, please contact us at support@grindon.fitness.</p>
        <p>When you visit our mobile application, and use our services, you trust us with your personal information. We take your privacy very seriously. In this privacy notice, we describe our privacy policy. We seek to explain to you in the clearest way possible what information we collect, how we use it and what rights you have in relation to it.</p>
        <p>If there are any terms in this privacy policy that you do not agree with, please discontinue use of our Apps and our services.</p>
      </motion.section>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
      >
        <h2>2. Information We Collect</h2>
        <p>We collect personal information that you voluntarily provide to us when expressing an interest in obtaining information about us or our products and services, when participating in activities on the App or otherwise contacting us.</p>
        
        <h3>Personal Data</h3>
        <p>The personal information that we collect depends on the context of your interactions with us and the App, the choices you make and the products and features you use. The personal information we collect can include the following:</p>
        <ul>
          <li><strong>Name and Contact Data:</strong> We collect your first and last name, email address, postal address, phone number, and other similar contact data.</li>
          <li><strong>Credentials:</strong> We collect passwords, password hints, and similar security information used for authentication and account access.</li>
        </ul>

        <h3>Health and Fitness Data</h3>
        <p>In order to provide personalized fitness plans and progress tracking, we may collect:</p>
        <ul>
          <li>Physical characteristics (e.g., height, weight, body measurements).</li>
          <li>Fitness activity data (e.g., workout types, duration, intensity).</li>
          <li>Nutrition and dietary information.</li>
          <li>Sleep patterns and overall wellness metrics.</li>
        </ul>
        <p><strong>Note for Apple HealthKit and Google Fit users:</strong> If you grant us permission, we may sync your data with these services. We do not use or share HealthKit or Google Fit data for marketing or advertising purposes.</p>

        <h3>Usage and Device Data</h3>
        <p>When you access our App, we may collect certain information automatically, including, but not limited to, the type of mobile device you use, your mobile device's unique ID, the IP address of your mobile device, your mobile operating system, the type of mobile Internet browser you use, and unique device identifiers.</p>
      </motion.section>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
      >
        <h2>3. How We Use Your Information</h2>
        <p>We use personal information collected via our App for a variety of business purposes described below:</p>
        <ul>
          <li><strong>To facilitate account creation and logon process:</strong> If you choose to link your account with us to a third party account (such as your Google or Facebook account), we use the information you allowed us to collect from those third parties to facilitate account creation and logon process.</li>
          <li><strong>To send administrative information to you:</strong> We may use your personal information to send you product, service and new feature information and/or information about changes to our terms, conditions, and policies.</li>
          <li><strong>To fulfill and manage your orders:</strong> We may use your information to fulfill and manage your orders, payments, returns, and exchanges made through the App.</li>
          <li><strong>To deliver services to the user:</strong> We may use your information to provide you with the requested service, such as fitness tracking and workout recommendations.</li>
          <li><strong>To respond to user inquiries/offer support to users:</strong> We may use your information to respond to your inquiries and solve any potential issues you might have with the use of our services.</li>
        </ul>
      </motion.section>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.4 }}
      >
        <h2>4. Will Your Information Be Shared With Anyone?</h2>
        <p>We only share information with your consent, to comply with laws, to provide you with services, to protect your rights, or to fulfill business obligations. We do not sell, rent, or trade any of your personal information with third parties for their promotional purposes.</p>
        <p>We may process or share data based on the following legal basis:</p>
        <ul>
          <li><strong>Consent:</strong> We may process your data if you have given us specific consent to use your personal information for a specific purpose.</li>
          <li><strong>Legitimate Interests:</strong> We may process your data when it is reasonably necessary to achieve our legitimate business interests.</li>
          <li><strong>Performance of a Contract:</strong> Where we have entered into a contract with you, we may process your personal information to fulfill the terms of our contract.</li>
          <li><strong>Legal Obligations:</strong> We may disclose your information where we are legally required to do so in order to comply with applicable law, governmental requests, a judicial proceeding, court order, or legal process.</li>
        </ul>
      </motion.section>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.5 }}
      >
        <h2>5. Data Retention Policy</h2>
        <p>We will only keep your personal information for as long as it is necessary for the purposes set out in this privacy policy, unless a longer retention period is required or permitted by law (such as tax, accounting or other legal requirements).</p>
        <p><strong>Active Accounts:</strong> We retain your data as long as your account is active to provide you with continuous service and progress history.</p>
        <p><strong>Account Deletion:</strong> Upon your request to delete your account, we will delete or anonymize your personal information from our active databases within 30 days. However, some information may be retained in our backups to prevent fraud, troubleshoot problems, assist with any investigations, enforce our Terms of Use and/or comply with legal requirements.</p>
        <p><strong>Inactive Data:</strong> If your account remains inactive for more than 24 months, we reserve the right to delete your personal data after providing notification to your registered email address.</p>
      </motion.section>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.6 }}
      >
        <h2>6. How Do We Keep Your Information Safe?</h2>
        <p>We have implemented appropriate technical and organizational security measures designed to protect the security of any personal information we process. However, please also remember that we cannot guarantee that the internet itself is 100% secure. Although we will do our best to protect your personal information, transmission of personal information to and from our App is at your own risk. You should only access the services within a secure environment.</p>
      </motion.section>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.7 }}
      >
        <h2>7. Your Privacy Rights</h2>
        <p>In some regions (like the European Economic Area), you have rights that allow you greater access to and control over your personal information. You may review, change, or terminate your account at any time.</p>
        <ul>
          <li><strong>Account Information:</strong> You may at any time review or change the information in your account or terminate your account by logging into your account settings and updating your profile.</li>
          <li><strong>Right to be Forgotten:</strong> You can request the deletion of your personal data by contacting us or using the "Delete Account" feature within the App settings.</li>
          <li><strong>Opting Out:</strong> You can unsubscribe from our marketing email list at any time by clicking on the unsubscribe link in the emails that we send or by contacting us.</li>
        </ul>
      </motion.section>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.8 }}
      >
        <h2>8. Children's Privacy</h2>
        <p>We do not knowingly solicit data from or market to children under 13 years of age. By using the App, you represent that you are at least 13 or that you are the parent or guardian of such a minor and consent to such minor dependent’s use of the App. If we learn that personal information from users less than 13 years of age has been collected, we will deactivate the account and take reasonable measures to promptly delete such data from our records.</p>
      </motion.section>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.9 }}
      >
        <h2>9. Updates To This Policy</h2>
        <p>We may update this privacy policy from time to time. The updated version will be indicated by an updated “Revised” date and the updated version will be effective as soon as it is accessible. If we make material changes to this privacy policy, we may notify you either by prominently posting a notice of such changes or by directly sending you a notification.</p>
      </motion.section>

      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 1.0 }}
      >
        <h2>10. Contact Us</h2>
        <p>If you have questions or comments about this policy, you may email us at <strong>support@grindon.fitness</strong> or by post to:</p>
        <p>
          Grindon Gym<br />
          Sallaghari, Bhaktapur<br />
          Bagmati, 44800<br />
          Nepal
        </p>
      </motion.section>
    </motion.div>
  );
};

export default PrivacyPolicy;
